import { ListGroups } from '../api/groups-endpoint';
import { createContext, useCallback, useMemo, useState, useContext } from 'react';
import { ContextData, ContextProviderProps } from '../types/context';
import { GroupIdentifier } from '../types/group';

const GroupsContext = createContext<ContextData<GroupIdentifier> | undefined>(undefined);
GroupsContext.displayName = 'GroupsContext';

function useGroupContextData(): ContextData<GroupIdentifier> {
  const [groups, setGroups] = useState<GroupIdentifier[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<GroupIdentifier[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    const listGroupsResponse = await ListGroups();
    if (listGroupsResponse.success && listGroupsResponse.data) {
      setGroups(listGroupsResponse.data);
    } else {
      setGroups([]);
    }
    setIsLoading(false);
  }, [setGroups]);

  const filterGroups = useCallback(
    async (filter: string) => {
      if (filter === '') {
        setFilteredGroups(undefined);
        return;
      }

      const filterResult = groups.filter((group) => {
        return group.Name?.toLowerCase().includes(filter.toLowerCase());
      });

      setFilteredGroups(filterResult);
    },
    [setFilteredGroups, groups],
  );

  return useMemo(
    () => ({
      data: filteredGroups ?? groups,
      isLoading,
      fetchData: fetchGroups,
      filterData: filterGroups,
    }),
    [groups, filteredGroups, isLoading, fetchGroups, filterGroups],
  );
}

export const useGroupsContext = (): ContextData<GroupIdentifier> => {
  const groupsContext = useContext(GroupsContext);
  if (!groupsContext) {
    throw new Error('useGroupsContext must be used within the GroupsContext.Provider');
  }
  return groupsContext;
};

export function GroupsContextProvider({ children }: ContextProviderProps): JSX.Element {
  const groupContextData = useGroupContextData();
  return <GroupsContext.Provider value={groupContextData}>{children}</GroupsContext.Provider>;
}

export default { useGroupsContext, GroupsContextProvider };
